import { css, SerializedStyles } from '@emotion/react';
import { theme } from '@frontend/theme';

const chipVariantOptions = [
  'primary',
  'critical',
  'success',
  'warn',
  'active',
  'eggplant',
  'seaweed',
  'disabled',
  'interactive',
  'neutral',
  'outline',
  'default',
  'primaryDark',
  'warningDark',
  'warningNeutral',
  'indigo',
  'tangerine',
  'rose',
] as const;
export type Variants = (typeof chipVariantOptions)[number];

const tagColorOptions = ['blue', 'green', 'teal', 'yellow', 'red', 'purple', 'gray'] as const;
export type TagColor = (typeof tagColorOptions)[number];

const statusSwitcherVariantOptions = [
  'default',
  'success',
  'warning',
  'critical',
  'seaweed',
  'eggplant',
  'neutral',
  'disabled',
] as const;
export type StatusSwitcherVariants = (typeof statusSwitcherVariantOptions)[number];

const categoryChipVariantOptions = [
  'blue',
  'indigo',
  'eggplant',
  'rose',
  'red',
  'tangerine',
  'yellow',
  'green',
  'seaweed',
  'neutral',
  'disabled',
] as const;
export type CategoryChipVariants = (typeof categoryChipVariantOptions)[number];

export type ChipStylesProps = {
  variant: Variants;
  size: 'tiny' | 'default' | 'large';
  isResponsive?: boolean;
  maxWidth: string;
};

export const variantStylesMap: Record<Variants, SerializedStyles> = {
  primary: css`
    background: ${theme.colors.primary10};
    border-radius: ${theme.borderRadius.small};
    color: ${theme.colors.primary50};
  `,
  critical: css`
    background: ${theme.colors.critical10};
    color: ${theme.colors.critical50};
  `,
  success: css`
    background: ${theme.colors.success10};
    color: ${theme.colors.success50};
  `,
  warn: css`
    background: ${theme.colors.warning5};
    color: ${theme.colors.warning80};
  `,
  active: css`
    background: ${theme.colors.warning5};
    color: ${theme.colors.warning70};
  `,
  eggplant: css`
    background: ${theme.colors.secondary.eggplant10};
    color: ${theme.colors.secondary.eggplant50};
  `,
  seaweed: css`
    background: ${theme.colors.secondary.seaweed10};
    color: ${theme.colors.secondary.seaweed50};
  `,
  disabled: css`
    background: ${theme.colors.neutral10};
    color: ${theme.colors.neutral30};
  `,
  interactive: css`
    background-color: ${theme.colors.primary5};
    border: none;
    color: ${theme.colors.neutral90};
  `,
  neutral: css`
    background: ${theme.colors.neutral10};
    color: ${theme.colors.neutral50};
  `,
  outline: css`
    border: 1px solid ${theme.colors.neutral50};
    color: ${theme.colors.neutral50};
  `,
  default: css`
    background: ${theme.colors.neutral5};
    color: ${theme.colors.neutral50};
  `,
  primaryDark: css`
    background: ${theme.colors.primary20};
    color: ${theme.colors.primary80};
  `,
  warningDark: css`
    background: ${theme.colors.warning50};
    color: ${theme.colors.warning80};
  `,
  warningNeutral: css`
    background: ${theme.colors.warning20};
    color: ${theme.colors.warning70};
  `,
  indigo: css`
    background: ${theme.colors.indigo10};
    color: ${theme.colors.indigo60};
  `,
  tangerine: css`
    background: ${theme.colors.tangerine10};
    color: ${theme.colors.tangerine60};
  `,
  rose: css`
    background: ${theme.colors.rose10};
    color: ${theme.colors.rose60};
  `,
};

const variantsStyle = (variant: Variants) => {
  return variantStylesMap[variant] || css``;
};

const style = ({ variant, size, isResponsive, maxWidth = '120px' }: ChipStylesProps) => [
  css`
    align-items: center;
    border-radius: ${theme.borderRadius.small};
    background: ${theme.colors.white};
    display: inline-flex;
    justify-content: center;
    gap: ${theme.spacing(0.75)};
    height: 24px;
    padding: ${theme.spacing(0.25, 1)};
    white-space: nowrap;
    max-width: ${isResponsive ? `min(100%, ${maxWidth})` : maxWidth};
    font-size: ${theme.font.size.medium};
    .chip-text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin: 0;
    }
    :focus {
      outline: 2px solid ${theme.colors.primary50};
      outline-offset: 1px;
    }
    @supports (selector(:has(*))) {
      :focus-within:has(input) {
        outline: 2px solid ${theme.colors.primary50};
        outline-offset: 1px;
      }
    }
    @supports not (selector(:has(*))) {
      :focus-within {
        outline: 2px solid ${theme.colors.primary50};
        outline-offset: 1px;
      }
    }
    button {
      :focus {
        outline: 2px solid ${theme.colors.primary50};
        border-radius: ${theme.borderRadius.small};
      }
    }
    svg:first-of-type {
      flex-shrink: 0;
    }
  `,
  variantsStyle(variant),
  size === 'large' &&
    css`
      height: 40px;
    `,
  size === 'tiny' &&
    css`
      font-size: ${theme.fontSize(12)};
      padding: 0 ${theme.spacing(0.5)};
      height: ${theme.spacing(2)};
    `,
];

export type TagChipStylesProps = {
  color: TagColor;
  hasOnClick: boolean;
};

export const tagVariantStylesMap: Record<TagColor, { styles: SerializedStyles; hoverStyles: SerializedStyles }> = {
  blue: {
    styles: css`
      background-color: ${theme.colors.primary5};
      border: 1px solid ${theme.colors.primary70};
      color: ${theme.colors.primary70};
      svg {
        fill: ${theme.colors.primary70};
      }
    `,
    hoverStyles: css`
      :hover {
        background-color: ${theme.colors.primary10};
      }
    `,
  },
  green: {
    styles: css`
      background-color: ${theme.colors.success5};
      border: 1px solid ${theme.colors.success70};
      color: ${theme.colors.success70};
      svg {
        fill: ${theme.colors.success70};
      }
    `,
    hoverStyles: css`
      :hover {
        background-color: ${theme.colors.success10};
      }
    `,
  },
  teal: {
    styles: css`
      background-color: ${theme.colors.secondary.seaweed5};
      border: 1px solid ${theme.colors.secondary.seaweed70};
      color: ${theme.colors.secondary.seaweed70};
      svg {
        fill: ${theme.colors.secondary.seaweed70};
      }
    `,
    hoverStyles: css`
      :hover {
        background-color: ${theme.colors.secondary.seaweed10};
      }
    `,
  },
  yellow: {
    styles: css`
      background-color: ${theme.colors.warning5};
      border: 1px solid ${theme.colors.warning80};
      color: ${theme.colors.warning80};
      svg {
        fill: ${theme.colors.warning80};
      }
    `,
    hoverStyles: css`
      :hover {
        background-color: ${theme.colors.warning50};
      }
    `,
  },
  red: {
    styles: css`
      background-color: ${theme.colors.critical5};
      border: 1px solid ${theme.colors.critical70};
      color: ${theme.colors.critical70};
      svg {
        fill: ${theme.colors.critical70};
      }
    `,
    hoverStyles: css`
      :hover {
        background-color: ${theme.colors.critical10};
      }
    `,
  },
  purple: {
    styles: css`
      background-color: ${theme.colors.secondary.eggplant5};
      border: 1px solid ${theme.colors.secondary.eggplant70};
      color: ${theme.colors.secondary.eggplant70};
      svg {
        fill: ${theme.colors.secondary.eggplant70};
      }
    `,
    hoverStyles: css`
      :hover {
        background-color: ${theme.colors.secondary.eggplant10};
      }
    `,
  },
  gray: {
    styles: css`
      background-color: ${theme.colors.neutral10};
      border: 1px solid ${theme.colors.neutral70};
      color: ${theme.colors.neutral70};
      svg {
        fill: ${theme.colors.neutral70};
      }
    `,
    hoverStyles: css`
      :hover {
        background-color: ${theme.colors.neutral20};
      }
    `,
  },
};

const getTagVariantStyles = (color: TagColor) => {
  return tagVariantStylesMap[color] || { styles: css``, hoverStyles: css`` };
};

const tagStyle = ({ color, hasOnClick }: TagChipStylesProps) => {
  const variantStyles = getTagVariantStyles(color);
  return [
    css`
      cursor: ${hasOnClick ? 'pointer' : 'default'};
    `,
    variantStyles.styles,
    hasOnClick && variantStyles.hoverStyles,
  ];
};

export type DropdownFilterChipStylesProps = {
  filterIsActive: boolean;
  isOpen: boolean;
};

const dropdownFilterStyle = ({ filterIsActive, isOpen }: DropdownFilterChipStylesProps) => [
  css`
    cursor: pointer;
    & .location-caret {
      transition: transform 250ms ease-in-out;
    }
  `,
  filterIsActive
    ? css`
        background-color: ${theme.colors.primary50};
        border: 1px solid ${theme.colors.primary50};

        * {
          color: ${theme.colors.white};
          fill: ${theme.colors.white};
        }
      `
    : css`
        background-color: transparent;
        border: 1px solid ${theme.colors.neutral50};

        * {
          color: ${theme.colors.neutral90};
          fill: ${theme.colors.neutral90};
        }
      `,
  isOpen &&
    css`
      & .location-caret {
        transform: rotate(180deg);
      }
    `,
];

export type StatusSwitcherStyleProps = {
  variant: StatusSwitcherVariants;
};

export const statusSwitcherVariantColors: Record<
  StatusSwitcherVariants,
  {
    color: string;
    background: string;
    hoverBackground: string;
    borderColor: string;
  }
> = {
  default: {
    color: theme.colors.neutral90,
    background: theme.colors.white,
    hoverBackground: theme.colors.neutral10,
    borderColor: theme.colors.neutral60,
  },
  success: {
    color: theme.colors.success60,
    background: theme.colors.success5,
    hoverBackground: theme.colors.success10,
    borderColor: theme.colors.success60,
  },
  warning: {
    color: theme.colors.warning60,
    background: theme.colors.warning5,
    hoverBackground: theme.colors.warning10,
    borderColor: theme.colors.warning60,
  },
  critical: {
    color: theme.colors.critical60,
    background: theme.colors.critical5,
    hoverBackground: theme.colors.critical10,
    borderColor: theme.colors.critical60,
  },
  seaweed: {
    color: theme.colors.secondary.seaweed60,
    background: theme.colors.secondary.seaweed5,
    hoverBackground: theme.colors.secondary.seaweed10,
    borderColor: theme.colors.secondary.seaweed60,
  },
  eggplant: {
    color: theme.colors.secondary.eggplant60,
    background: theme.colors.secondary.eggplant5,
    hoverBackground: theme.colors.secondary.eggplant10,
    borderColor: theme.colors.secondary.eggplant60,
  },
  neutral: {
    color: theme.colors.neutral60,
    background: theme.colors.neutral5,
    hoverBackground: theme.colors.neutral10,
    borderColor: theme.colors.neutral60,
  },
  disabled: {
    color: theme.colors.neutral20,
    background: theme.colors.neutral5,
    borderColor: 'transparent',
    hoverBackground: theme.colors.neutral5,
  },
};

const getStatusSwitcherVariantStyles = (variant: StatusSwitcherVariants) => {
  const { color, background, borderColor, hoverBackground } = statusSwitcherVariantColors[variant];

  return css`
    ${variant !== 'disabled'
      ? `
    background: ${background};
    color: ${color};
    border: 1px solid ${borderColor};
    :hover {
      background-color: ${hoverBackground};
      border-color: transparent;
    }
    :focus {
      border: 1px solid ${color};
      background: ${background};
    }
    `
      : `
      background: ${background};
      color: ${color};
      border: 1px solid ${borderColor}; // Ensure border is applied for disabled as well
      `}
  `;
};

const statusSwitcherStyle = ({ variant }: StatusSwitcherStyleProps) => [
  css`
    cursor: pointer;
    max-width: 114px;
    font-size: ${theme.font.size.small};
    gap: ${theme.spacing(0.5)};
  `,
  getStatusSwitcherVariantStyles(variant),
];

export type CategoryChipStylesProps = {
  variant: CategoryChipVariants;
  indicatorColor?: string;
};

const categoryChipVariantColors: Record<CategoryChipVariants, { color: string }> = {
  blue: { color: theme.colors.primary50 },
  indigo: { color: theme.colors.indigo40 },
  eggplant: { color: theme.colors.secondary.eggplant50 },
  rose: { color: theme.colors.rose40 },
  red: { color: theme.colors.critical50 },
  tangerine: { color: theme.colors.tangerine40 },
  yellow: { color: theme.colors.warning20 },
  green: { color: theme.colors.success40 },
  seaweed: { color: theme.colors.secondary.seaweed40 },
  neutral: { color: theme.colors.neutral40 },
  disabled: { color: theme.colors.neutral20 },
};

const categoryChipStyle = ({ variant, indicatorColor }: CategoryChipStylesProps) => [
  css`
    background-color: ${theme.colors.neutral5};
    color: ${variant !== 'disabled' ? theme.colors.neutral90 : categoryChipVariantColors[variant].color};
    gap: ${theme.spacing(0.5)};
    max-width: none;

    .category-chip-indicator {
      width: ${theme.spacing(1)};
      height: ${theme.spacing(1)};
      border-radius: 50%;
      background-color: ${indicatorColor ?? categoryChipVariantColors[variant].color};
    }
  `,
];

// Timezone Chip Styles

const timezoneChipVariantOptions = ['neutral', 'neutralDark'] as const;

export type TimezoneChipVariant = (typeof timezoneChipVariantOptions)[number];

type TimezoneChipStyleProps = {
  variant: TimezoneChipVariant;
};

const timezoneChipVariantColors: Record<TimezoneChipVariant, { backgroundColor: string; color: string }> = {
  neutral: {
    backgroundColor: theme.colors.neutral20,
    color: theme.colors.neutral50,
  },
  neutralDark: {
    backgroundColor: theme.colors.neutral30,
    color: theme.colors.neutral90,
  },
};

const timezoneChipStyle = ({ variant }: TimezoneChipStyleProps) => css`
  height: 16px;
  background: ${timezoneChipVariantColors[variant].backgroundColor};
  padding: ${theme.spacing(0.25)};
  gap: ${theme.spacing(0.25)};

  & > svg {
    color: ${timezoneChipVariantColors[variant].color};
  }

  .chip-text {
    color: ${timezoneChipVariantColors[variant].color};
    font-weight: ${theme.font.weight.bold};
    font-size: ${theme.font.size.small};
  }
`;

// Tasks Chip Styles

const taskChipVariantOptions = ['task', 'done'] as const;

export type TaskChipVariant = (typeof taskChipVariantOptions)[number];

type TaskChipStyleProps = {
  variant: TaskChipVariant;
};

const taskChipVariantColors: Record<TaskChipVariant, { backgroundColor: string; borderColor: string; color: string }> =
  {
    task: {
      backgroundColor: theme.colors.tangerine40,
      borderColor: theme.colors.tangerine50,
      color: theme.colors.white,
    },
    done: {
      backgroundColor: theme.colors.success5,
      borderColor: theme.colors.success50,
      color: theme.colors.success80,
    },
  };

const taskChipStyle = ({ variant }: TaskChipStyleProps) => css`
  background: ${taskChipVariantColors[variant].backgroundColor};
  border: 1px solid ${taskChipVariantColors[variant].borderColor};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing(0.5)};
  width: auto;

  & > svg {
    color: ${taskChipVariantColors[variant].color};
    flex-shrink: 0;
  }

  .chip-text {
    color: ${taskChipVariantColors[variant].color};
    font-weight: ${theme.font.weight.bold};
    max-width: 70px;
  }

  span {
    color: ${taskChipVariantColors[variant].color};
    font-weight: ${theme.font.weight.bold};
  }
`;

const switchFilterChipStyle = (isSelected?: boolean) => css`
  max-width: 144px;
  width: fit-content;
  padding: ${theme.spacing(1)};
  cursor: pointer;
  height: ${theme.spacing(5)};
  color: ${theme.colors.neutral90};
  background-color: ${theme.colors.white};
  outline: 1px solid ${theme.colors.neutral50};
  border: 0;

  ${isSelected &&
  `
      background-color: ${theme.colors.primary5};
      border: 1px solid ${theme.colors.primary40};
    `}

  .chip-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: 0;
  }

  :hover {
    background-color: ${isSelected ? theme.colors.primary20 : theme.colors.neutral10};
    outline: ${isSelected ? `1px solid ${theme.colors.primary}` : '0'};
  }

  :disabled {
    background-color: ${theme.colors.white};
    color: ${theme.colors.neutral20};
    outline: 1px solid ${theme.colors.neutral20};
  }

  :focus {
    background-color: ${theme.colors.white};
    border: 1px solid ${theme.colors.neutral90};
    border-radius: 8px;
  }
`;

export const ChipStyles = {
  chip: (props: ChipStylesProps) => style(props),
  tagChip: (props: TagChipStylesProps) => tagStyle(props),
  dropdownFilterChip: (props: DropdownFilterChipStylesProps) => dropdownFilterStyle(props),
  statusSwitcherChip: (props: StatusSwitcherStyleProps) => statusSwitcherStyle(props),
  categoryChip: (props: CategoryChipStylesProps) => categoryChipStyle(props),
  timezoneChip: (props: TimezoneChipStyleProps) => timezoneChipStyle(props),
  switchFilterChip: (isSelected?: boolean) => switchFilterChipStyle(isSelected),
  taskChip: (props: TaskChipStyleProps) => taskChipStyle(props),
};
